import api from '../api/client';
// pages/StockTransactionPage.js
import React, { useState, useEffect } from 'react';
import {
    Card,
    Button,
    Form,
    Input,
    InputNumber,
    Select,
    DatePicker,
    Row,
    Col,
    Space,
    Tag,
    message,
    Divider,
    Breadcrumb,
    Statistic,
    Alert,
    Descriptions,
    Modal
} from 'antd';
import {
    ArrowLeftOutlined,
    SaveOutlined,
    PlusOutlined,
    DeleteOutlined,
    ShoppingOutlined,
    HomeOutlined,
    CalculatorOutlined
} from '@ant-design/icons';
import { Link, useParams } from 'react-router-dom';
import moment from 'moment';
import ProTable from '@ant-design/pro-table';

const { Option } = Select;
const { TextArea } = Input;

const StockTransactionPage = () => {
    const { type } = useParams(); // 'in' or 'out'
    // const navigate = useNavigate();
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const [items, setItems] = useState([]);
    const [totalAmount, setTotalAmount] = useState(0);
    const [stakeholderInfo, setStakeholderInfo] = useState(null);

    // Transaction type
    const transactionType = type === 'in' ? 'stock_in' : 'stock_out';

    // Product types with categories
    const productTypes = {
        feed: {
            label: 'Feed',
            color: 'green',
            categories: [
                { value: 'ready', label: 'Ready Feed' },
                { value: 'raw', label: 'Raw Material' },
                { value: 'concentrate', label: 'Concentrate' },
                { value: 'supplement', label: 'Feed Supplement' }
            ]
        },
        vaccine: {
            label: 'Vaccine',
            color: 'red',
            categories: [
                { value: 'poultry', label: 'Poultry Vaccine' },
                { value: 'livestock', label: 'Livestock Vaccine' },
                { value: 'general', label: 'General Vaccine' }
            ]
        },
        supplement: {
            label: 'Supplement',
            color: 'blue',
            categories: [
                { value: 'vitamin', label: 'Vitamin' },
                { value: 'mineral', label: 'Mineral' },
                { value: 'probiotic', label: 'Probiotic' },
                { value: 'antibiotic', label: 'Antibiotic' }
            ]
        },
        food: {
            label: 'Food',
            color: 'orange',
            categories: [
                { value: 'grains', label: 'Grains' },
                { value: 'fruits', label: 'Fruits' },
                { value: 'vegetables', label: 'Vegetables' },
                { value: 'processed', label: 'Processed Food' }
            ]
        },
        water: {
            label: 'Water',
            color: 'cyan',
            categories: [
                { value: 'drinking', label: 'Drinking Water' },
                { value: 'mineral', label: 'Mineral Water' },
                { value: 'treated', label: 'Treated Water' }
            ]
        }
    };

    // Units by product type
    const unitsByType = {
        feed: ['kg', 'g', 'bags', 'tons'],
        vaccine: ['doses', 'bottles', 'vials', 'packets'],
        supplement: ['kg', 'g', 'tablets', 'capsules', 'bottles'],
        food: ['kg', 'g', 'pieces', 'boxes', 'crates'],
        water: ['liters', 'ml', 'bottles', 'gallons']
    };

    // Payment methods
    const paymentMethods = [
        { value: 'cash', label: 'Cash' },
        { value: 'mpesa', label: 'M-Pesa' },
        { value: 'bank', label: 'Bank Transfer' },
        { value: 'credit', label: 'Credit' }
    ];

    // Fetch stakeholder info
    const fetchStakeholderInfo = async (stakeholderId) => {
        try {
            // This would be your API call to get stakeholder details
            // const response = await fetch(`/api/stakeholders/${stakeholderId}`);
            // const result = await response.json();

            // Mock data for demonstration
            if (stakeholderId) {
                setStakeholderInfo({
                    id: stakeholderId,
                    name: `Customer ${stakeholderId}`,
                    type: 'Supplier',
                    contact: '+254 700 000 000'
                });
            }
        } catch (error) {
            console.error('Error fetching stakeholder info:', error);
        }
    };

    // Calculate total amount
    useEffect(() => {
        const total = items.reduce((sum, item) => sum + (item.totalPrice || 0), 0);
        setTotalAmount(total);
        form.setFieldsValue({ totalAmount: total });
    }, [items, form]);

    // Handle stakeholder ID change
    const handleStakeholderChange = (value) => {
        if (value) {
            fetchStakeholderInfo(value);
        } else {
            setStakeholderInfo(null);
        }
    };

    // Add new item
    const handleAddItem = () => {
        const newItem = {
            id: Date.now(),
            productType: 'feed',
            productCategory: 'ready',
            productName: '',
            brand: '',
            batchNumber: '',
            quantity: 1,
            unit: 'kg',
            unitPrice: 0,
            totalPrice: 0
        };
        setItems([...items, newItem]);
    };

    // Update item
    const handleUpdateItem = (id, field, value) => {
        setItems(
            items.map((item) => {
                if (item.id === id) {
                    const updatedItem = { ...item, [field]: value };

                    // Auto-calculate total price if quantity or unit price changes
                    if (field === 'quantity' || field === 'unitPrice') {
                        updatedItem.totalPrice = (updatedItem.quantity || 0) * (updatedItem.unitPrice || 0);
                    }

                    return updatedItem;
                }
                return item;
            })
        );
    };

    // Delete item
    const handleDeleteItem = (id) => {
        setItems(items.filter((item) => item.id !== id));
    };

    // Get categories for product type
    const getCategories = (productType) => {
        return productTypes[productType]?.categories || [];
    };

    // Get units for product type
    const getUnits = (productType) => {
        return unitsByType[productType] || ['units'];
    };

    // Save transaction
    const handleSaveTransaction = async (values) => {
        if (items.length === 0) {
            message.error('Please add at least one item to the transaction');
            return;
        }

        setSaving(true);
        try {
            const transactionData = {
                ...values,
                transactionType: transactionType,
                transactionDate: values.transactionDate.format('YYYY-MM-DD'),
                recordedBy: 'Admin', // This should come from user context
                userId: 1, // This should come from user context
                userName: 'Admin', // This should come from user context
                totalAmount: totalAmount,
                items: items.map((item) => ({
                    ...item,
                    // Remove temporary ID for backend
                    id: undefined
                }))
            };

            const response = await fetch('/api/stock/transactions', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(transactionData)
            });

            const result = await response.json();

            if (result.success) {
                message.success(`Stock ${type === 'in' ? 'in' : 'out'} transaction created successfully`);
                // navigate('/stock');
            } else {
                throw new Error(result.message);
            }
        } catch (error) {
            console.error('Error creating transaction:', error);
            message.error('Failed to create transaction');
        } finally {
            setSaving(false);
        }
    };

    // Go back to stock management
    const handleGoBack = () => {
        // navigate('/stock');
    };

    // Columns for ProTable
    const columns = [
        {
            title: 'Product Type',
            dataIndex: 'productType',
            key: 'productType',
            width: 120,
            render: (_, record) => (
                <Select
                    value={record.productType}
                    onChange={(value) => handleUpdateItem(record.id, 'productType', value)}
                    style={{ width: '100%' }}
                >
                    {Object.entries(productTypes).map(([key, type]) => (
                        <Option key={key} value={key}>
                            <Tag color={type.color}>{type.label}</Tag>
                        </Option>
                    ))}
                </Select>
            )
        },
        {
            title: 'Category',
            dataIndex: 'productCategory',
            key: 'productCategory',
            width: 120,
            render: (_, record) => (
                <Select
                    value={record.productCategory}
                    onChange={(value) => handleUpdateItem(record.id, 'productCategory', value)}
                    style={{ width: '100%' }}
                >
                    {getCategories(record.productType).map((category) => (
                        <Option key={category.value} value={category.value}>
                            {category.label}
                        </Option>
                    ))}
                </Select>
            )
        },
        {
            title: 'Product Name',
            dataIndex: 'productName',
            key: 'productName',
            width: 150,
            render: (_, record) => (
                <Input
                    value={record.productName}
                    onChange={(e) => handleUpdateItem(record.id, 'productName', e.target.value)}
                    placeholder="Enter product name"
                />
            )
        },
        {
            title: 'Brand',
            dataIndex: 'brand',
            key: 'brand',
            width: 120,
            render: (_, record) => (
                <Input value={record.brand} onChange={(e) => handleUpdateItem(record.id, 'brand', e.target.value)} placeholder="Brand" />
            )
        },
        {
            title: 'Batch No.',
            dataIndex: 'batchNumber',
            key: 'batchNumber',
            width: 120,
            render: (_, record) => (
                <Input
                    value={record.batchNumber}
                    onChange={(e) => handleUpdateItem(record.id, 'batchNumber', e.target.value)}
                    placeholder="Batch number"
                />
            )
        },
        {
            title: 'Quantity',
            dataIndex: 'quantity',
            key: 'quantity',
            width: 100,
            render: (_, record) => (
                <InputNumber
                    value={record.quantity}
                    onChange={(value) => handleUpdateItem(record.id, 'quantity', value)}
                    min={0.01}
                    step={0.01}
                    style={{ width: '100%' }}
                />
            )
        },
        {
            title: 'Unit',
            dataIndex: 'unit',
            key: 'unit',
            width: 100,
            render: (_, record) => (
                <Select value={record.unit} onChange={(value) => handleUpdateItem(record.id, 'unit', value)} style={{ width: '100%' }}>
                    {getUnits(record.productType).map((unit) => (
                        <Option key={unit} value={unit}>
                            {unit}
                        </Option>
                    ))}
                </Select>
            )
        },
        {
            title: 'Unit Price (KES)',
            dataIndex: 'unitPrice',
            key: 'unitPrice',
            width: 120,
            render: (_, record) => (
                <InputNumber
                    value={record.unitPrice}
                    onChange={(value) => handleUpdateItem(record.id, 'unitPrice', value)}
                    min={0}
                    step={0.01}
                    formatter={(value) => `KES ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={(value) => value.replace(/KES\s?|(,*)/g, '')}
                    style={{ width: '100%' }}
                />
            )
        },
        {
            title: 'Total Price (KES)',
            dataIndex: 'totalPrice',
            key: 'totalPrice',
            width: 120,
            render: (_, record) => (
                <InputNumber
                    value={record.totalPrice}
                    formatter={(value) => `KES ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={(value) => value.replace(/KES\s?|(,*)/g, '')}
                    style={{ width: '100%' }}
                    disabled
                />
            )
        },
        {
            title: 'Storage Location',
            dataIndex: 'storageLocation',
            key: 'storageLocation',
            width: 120,
            render: (_, record) => (
                <Input
                    value={record.storageLocation}
                    onChange={(e) => handleUpdateItem(record.id, 'storageLocation', e.target.value)}
                    placeholder="Storage location"
                />
            )
        },
        {
            title: 'Action',
            key: 'action',
            width: 80,
            render: (_, record) => (
                <Button type="link" danger icon={<DeleteOutlined />} onClick={() => handleDeleteItem(record.id)}>
                    Remove
                </Button>
            )
        }
    ];

    return (
        <div style={{ padding: '24px' }}>
            {/* Breadcrumb */}
            <Breadcrumb style={{ marginBottom: '16px' }}>
                <Breadcrumb.Item>
                    <Link to="/">
                        <HomeOutlined /> Home
                    </Link>
                </Breadcrumb.Item>
                <Breadcrumb.Item>
                    <Link to="/stock">
                        <ShoppingOutlined /> Stock Management
                    </Link>
                </Breadcrumb.Item>
                <Breadcrumb.Item>Stock {type === 'in' ? 'In' : 'Out'}</Breadcrumb.Item>
            </Breadcrumb>

            {/* Page Header */}
            <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
                <Col>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <Button icon={<ArrowLeftOutlined />} onClick={handleGoBack} size="large">
                            Back to Stock
                        </Button>
                        <div>
                            <h1 style={{ margin: 0, fontSize: '24px' }}>Stock {type === 'in' ? 'In' : 'Out'} Transaction</h1>
                            <p style={{ margin: 0, color: '#666' }}>
                                {type === 'in' ? 'Add new stock to inventory' : 'Remove stock from inventory'}
                            </p>
                        </div>
                    </div>
                </Col>
                <Col>
                    <Space>
                        <Button onClick={handleGoBack} size="large">
                            Cancel
                        </Button>
                        <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={() => form.submit()} size="large">
                            Save Transaction
                        </Button>
                    </Space>
                </Col>
            </Row>

            <Form
                form={form}
                layout="vertical"
                onFinish={handleSaveTransaction}
                initialValues={{
                    transactionDate: moment(),
                    paymentStatus: 'paid'
                }}
            >
                <Row gutter={[16, 16]}>
                    {/* Transaction Information */}
                    <Col xs={24} lg={8}>
                        <Card title="Transaction Information">
                            <Form.Item
                                label="Transaction Date"
                                name="transactionDate"
                                rules={[{ required: true, message: 'Please select transaction date' }]}
                            >
                                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" placeholder="Select date" />
                            </Form.Item>

                            <Form.Item
                                label={type === 'in' ? 'Supplier ID' : 'Customer ID'}
                                name="stakeholderId"
                                rules={[{ required: true, message: `Please enter ${type === 'in' ? 'supplier' : 'customer'} ID` }]}
                            >
                                <InputNumber
                                    style={{ width: '100%' }}
                                    min={1}
                                    placeholder={`Enter ${type === 'in' ? 'supplier' : 'customer'} ID`}
                                    onChange={handleStakeholderChange}
                                />
                            </Form.Item>

                            {stakeholderInfo && (
                                <Alert
                                    message={stakeholderInfo.name}
                                    description={
                                        <div>
                                            <div>Type: {stakeholderInfo.type}</div>
                                            <div>Contact: {stakeholderInfo.contact}</div>
                                        </div>
                                    }
                                    type="info"
                                    showIcon
                                    style={{ marginBottom: '16px' }}
                                />
                            )}

                            <Form.Item label="Reference Number" name="referenceNumber">
                                <Input placeholder="Optional reference number" />
                            </Form.Item>

                            <Form.Item label="Delivery Person ID" name="deliveryPersonId">
                                <InputNumber style={{ width: '100%' }} min={1} placeholder="Delivery person ID" />
                            </Form.Item>
                        </Card>

                        <Card title="Payment Information" style={{ marginTop: '16px' }}>
                            <Form.Item label="Payment Method" name="paymentMethod">
                                <Select placeholder="Select payment method">
                                    {paymentMethods.map((method) => (
                                        <Option key={method.value} value={method.value}>
                                            {method.label}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item label="Payment Status" name="paymentStatus">
                                <Select placeholder="Select payment status">
                                    <Option value="paid">Paid</Option>
                                    <Option value="unpaid">Unpaid</Option>
                                    <Option value="partial">Partial</Option>
                                </Select>
                            </Form.Item>

                            <Form.Item label="Total Amount (KES)" name="totalAmount">
                                <InputNumber
                                    style={{ width: '100%' }}
                                    value={totalAmount}
                                    formatter={(value) => `KES ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    parser={(value) => value.replace(/KES\s?|(,*)/g, '')}
                                    disabled
                                />
                            </Form.Item>
                        </Card>
                    </Col>

                    {/* Items Section */}
                    <Col xs={24} lg={16}>
                        <Card
                            title={
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span>Stock Items ({items.length})</span>
                                    <Space>
                                        <Statistic
                                            title="Total Amount"
                                            value={totalAmount}
                                            precision={2}
                                            valueStyle={{ color: '#3f8600', fontSize: '18px' }}
                                            prefix="KES"
                                        />
                                        <Button type="primary" icon={<PlusOutlined />} onClick={handleAddItem}>
                                            Add Item
                                        </Button>
                                    </Space>
                                </div>
                            }
                        >
                            {items.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                                    <CalculatorOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
                                    <p>No items added to this transaction. Click "Add Item" to get started.</p>
                                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAddItem}>
                                        Add Your First Item
                                    </Button>
                                </div>
                            ) : (
                                <ProTable
                                    dataSource={items}
                                    columns={columns}
                                    rowKey="id"
                                    pagination={false}
                                    search={false}
                                    options={false}
                                    size="small"
                                    scroll={{ x: 1500 }}
                                    style={{ marginTop: '16px' }}
                                />
                            )}

                            {/* Additional Item Details */}
                            {items.length > 0 && (
                                <div style={{ marginTop: '16px' }}>
                                    <Divider />
                                    <h4>Additional Item Details</h4>
                                    <Row gutter={[16, 16]}>
                                        {items.map((item, index) => (
                                            <Col xs={24} key={item.id}>
                                                <Card size="small" title={`Item ${index + 1}: ${item.productName || 'Unnamed Product'}`}>
                                                    <Row gutter={16}>
                                                        <Col xs={24} sm={8}>
                                                            <Form.Item label="Expiration Date">
                                                                <DatePicker
                                                                    value={item.expirationDate ? moment(item.expirationDate) : null}
                                                                    onChange={(date) =>
                                                                        handleUpdateItem(
                                                                            item.id,
                                                                            'expirationDate',
                                                                            date ? date.format('YYYY-MM-DD') : null
                                                                        )
                                                                    }
                                                                    style={{ width: '100%' }}
                                                                    format="DD/MM/YYYY"
                                                                />
                                                            </Form.Item>
                                                        </Col>
                                                        <Col xs={24} sm={8}>
                                                            <Form.Item label="Manufacturing Date">
                                                                <DatePicker
                                                                    value={item.manufacturingDate ? moment(item.manufacturingDate) : null}
                                                                    onChange={(date) =>
                                                                        handleUpdateItem(
                                                                            item.id,
                                                                            'manufacturingDate',
                                                                            date ? date.format('YYYY-MM-DD') : null
                                                                        )
                                                                    }
                                                                    style={{ width: '100%' }}
                                                                    format="DD/MM/YYYY"
                                                                />
                                                            </Form.Item>
                                                        </Col>
                                                        <Col xs={24} sm={8}>
                                                            <Form.Item label="Item Notes">
                                                                <Input
                                                                    value={item.notes || ''}
                                                                    onChange={(e) => handleUpdateItem(item.id, 'notes', e.target.value)}
                                                                    placeholder="Item-specific notes"
                                                                />
                                                            </Form.Item>
                                                        </Col>
                                                    </Row>
                                                </Card>
                                            </Col>
                                        ))}
                                    </Row>
                                </div>
                            )}
                        </Card>

                        {/* Transaction Notes */}
                        <Card title="Additional Information" style={{ marginTop: '16px' }}>
                            <Form.Item label="Transaction Notes" name="notes">
                                <TextArea rows={4} placeholder="Additional notes about this transaction..." />
                            </Form.Item>
                        </Card>
                    </Col>
                </Row>
            </Form>

            {/* Action Buttons at Bottom */}
            <Divider />
            <Row justify="center" style={{ marginTop: '24px' }}>
                <Space>
                    <Button onClick={handleGoBack} size="large">
                        Cancel
                    </Button>
                    <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={() => form.submit()} size="large">
                        Save Transaction
                    </Button>
                </Space>
            </Row>
        </div>
    );
};

export default StockTransactionPage;
